<script lang="ts">
  import { secondsToTime } from '$lib/utils/time';
  import { onVisible } from '$lib/utils/visibility';
  import { createEventDispatcher, onMount } from 'svelte';

  export let data: number[];
  export let duration: number;
  export let color = '#CBD0D8';
  export let progressColor = '#EA0639';
  export let seekerColor = '#272C32';
  export let barWidth = 1;
  export let barGap = 0;
  export let height = 80;
  export let progress = 0;
  export let marginTop = '1rem';
  export let marginBottom = '1rem';

  let dispatch = createEventDispatcher<{ seek: number }>();
  let canvas: HTMLCanvasElement;
  let ctx: CanvasRenderingContext2D;
  let visible = false;
  let width = 0;
  let drawnData: number[] = [];
  let hoverX = 0;
  let hoverTime = '';

  function drawOverlay(from: number, to: number) {
    from = width * from;
    to = width * to;
    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = progressColor;
    ctx.fillRect(from, 0, Math.abs(from - to), height);
    ctx.restore();
  }

  function draw() {
    width = canvas.offsetWidth;
    const scaledWidth = width * window.devicePixelRatio;
    const scaledHeight = height * window.devicePixelRatio;

    canvas.setAttribute('width', scaledWidth.toString());
    canvas.setAttribute('height', scaledHeight.toString());
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const numBars = Math.floor(width / (barWidth + barGap));
    const centerY = height / 2;
    const maxDataValue = Math.max(...data.map(Math.abs));
    const scaleFactor = height / 2 / maxDataValue;

    ctx.fillStyle = color;

    for (let i = 0; i < numBars; i++) {
      const dataIndex = Math.floor((i * data.length) / numBars);
      const d = data[dataIndex];
      const x = i * (barWidth + barGap);

      let height = Math.abs(d * scaleFactor);
      // Prevent zero-height bars, we always want at least a 1px line
      if (height < 1) {
        height = 1 / window.devicePixelRatio;
      }
      ctx.fillRect(x, centerY - height, barWidth, height * 2);
    }
  }

  function updateHover(x: number, w: number) {
    if (x < 0) {
      x = 0;
    }
    if (x > w) {
      x = w;
    }
    hoverX = x;
    const seconds = (duration * x) / w;
    hoverTime = secondsToTime(seconds);
  }

  function hideHover() {
    hoverX = 0;
  }

  onMount(() => {
    ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    onVisible(canvas, () => {
      visible = true;
    });
  });

  $: if (visible && data && data !== drawnData) {
    drawnData = data;
    draw();
  }
  $: progress, visible && drawOverlay(0, progress);
</script>

<div
  class="container"
  style="
    margin-top: {marginTop};
    margin-bottom: {marginBottom};
  "
>
  <canvas
    bind:this={canvas}
    style="height: {height}px"
    aria-hidden="true"
    on:mousemove={(e) => updateHover(e.offsetX, canvas.offsetWidth)}
    on:mouseleave={hideHover}
    on:click={(e) => dispatch('seek', (duration * e.offsetX) / canvas.offsetWidth)}
 ></canvas>
  {#if hoverX}
    <div class="hover-line" style="background-color: {seekerColor}; left: {hoverX}px; top: {-marginTop}; bottom: {-marginBottom};"></div>
    <div class="hover-time" style="color: {seekerColor}; left: {hoverX}px">{hoverTime}</div>
  {/if}
</div>

<style lang="scss">
  canvas {
    width: 100%;
  }

  .container {
    position: relative;
    width: 100%;
    padding: 0;
    margin: 0;
    height: auto;
    display: flex;
  }

  .hover-line {
    position: absolute;
    top: -1rem;
    bottom: -1rem;
    width: 1px;
    pointer-events: none;
    display: block;
  }

  .hover-time {
    position: absolute;
    font-size: 12px;
    margin-left: 0.5rem;
    top: -1.25rem;
    pointer-events: none;
    display: block;
  }
</style>
